import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function BookingSuccess() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed

  const poll = useCallback(async (sessionId, attempt) => {
    if (attempt >= MAX_ATTEMPTS) {
      setState("failed");
      return;
    }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("success");
        toast.success("Payment successful! Your booking is confirmed.");
        return;
      }
      if (data.status === "expired") {
        setState("failed");
        return;
      }
      setTimeout(() => poll(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(sessionId, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll(sessionId, 0);
  }, [authLoading, user, navigate, poll]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="max-w-md w-full text-center border border-border rounded-2xl p-10 bg-white" data-testid="booking-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
            <h1 className="mt-6 text-2xl font-black">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="mt-6 text-2xl font-black">Booking confirmed!</h1>
            <p className="mt-2 text-muted-foreground">Your bike is reserved. Check your dashboard for pickup details.</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn" className="mt-6 w-full bg-primary hover:bg-[#1D4ED8] text-white font-bold h-11">
              Go to dashboard
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-destructive mx-auto" />
            <h1 className="mt-6 text-2xl font-black">Payment not completed</h1>
            <p className="mt-2 text-muted-foreground">We couldn't confirm your payment. You can try booking again.</p>
            <Button onClick={() => navigate("/bikes")} data-testid="failed-retry-btn" className="mt-6 w-full bg-primary hover:bg-[#1D4ED8] text-white font-bold h-11">
              Back to bikes
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
